import { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import Icon from '@ant-design/icons';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';

import { ContextWrapper } from 'contexts/wrapper.context';
import { createPage, editPage, getPage } from 'services/pages';
import { useModal } from 'contexts/modalProvider.context';
import { getPageName, visitPage } from 'utils/pages';
import { isDashboardArabic } from 'utils/intl-wrapper';
import { getEditChanges } from 'utils/helpers';
import { getPageTemplate } from 'constants/page-templates';

import Container from 'components/Container/Container';
import Header from 'components/Header/Header';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import BRButton from 'components/BRButton/BRButton';
import PageDetailsSection from 'components/Pages/components/CreateEditPage/components/PageDetailsSection/PageDetailsSection';
import PageContentSection from 'components/Pages/components/CreateEditPage/components/PageContentSection/PageContentSection';
import { notify } from 'components/Notify/Notify';
import ConfirmationModal from 'components/BRModals/ConfirmationModal/ConfirmationModal';

import { ReactComponent as CloseIcon } from 'assets/bosta-icons/Close.svg';

import './CreateEditPage.less';

const CreateEditPage = () => {
  const { id: pageId } = useParams();
  const { formatMessage } = useIntl();
  const formRef = useRef();
  const { businessInfo, setPreventNavigation } = useContext(ContextWrapper);
  const history = useHistory();
  const { openModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const [isContextLoading, setIsContextLoading] = useState(true);
  const [pageTitle, setPageTitle] = useState('');
  const [contentData, setContentData] = useState({
    content: '',
    contentAr: ''
  });
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [isPrimaryBtnDisabled, setIsPrimaryBtnDisabled] = useState(true);
  const [isURLFieldTouched, setIsURLFieldTouched] = useState(false);
  const [urlName, setURLName] = useState('');
  const [fetchedPageDetails, setFetchedPageDetails] = useState({});
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);

  const isEdit = !!pageId;
  const createOrEditMsgId = isEdit ? 'edit_page' : 'create_page';

  const fillPageForm = (page = {}) => {
    const { content, contentAr, url } = page;

    formRef.current.setFieldsValue(page);
    setURLName(url);
    setContentData({ content, contentAr });
    setPageTitle(getPageName(page));
    setSelectedTemplateKey(null);
  };

  const getPageDetails = async () => {
    setIsLoading(true);

    try {
      const page = (await getPage(pageId)) || {};
      delete page.isVisible;
      setFetchedPageDetails(page);

      fillPageForm(page);
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const isRequiredFieldsFilled = ({
    pageTitle,
    pageTitleAr,
    description,
    descriptionAr,
    url
  }) => (pageTitle || pageTitleAr) && (description || descriptionAr) && url;

  const getCleanedURL = (url = '') => {
    const regex = /[a-zA-Z0-9_\-\s]/g;
    const matchedURL = url.match(regex)?.join('');

    if (matchedURL) {
      return matchedURL.trim().toLowerCase().replaceAll(' ', '-');
    }

    return '';
  };

  const onValuesChange = (changedValue, allValues) => {
    const { url, pageTitle, pageTitleAr } = changedValue;

    if (isString(url)) {
      const cleanedURL = getCleanedURL(url);
      allValues.url = cleanedURL;
      formRef.current.setFieldsValue({
        url: cleanedURL
      });
      setURLName(cleanedURL);

      if (!isURLFieldTouched) {
        setIsURLFieldTouched(true);
      }
    }

    if (isString(pageTitle) && !isEdit) {
      setPageTitle(pageTitle);
      if (!isURLFieldTouched) {
        const cleanedURL = getCleanedURL(pageTitle);
        formRef.current.setFieldsValue({
          url: cleanedURL
        });
        setURLName(cleanedURL);
      }
    }

    if (pageTitleAr && isDashboardArabic()) {
      setPageTitle(pageTitleAr);
    }

    setIsPrimaryBtnDisabled(!isRequiredFieldsFilled(allValues));
    setIsFieldsTouched(true);
  };

  const goToPagesList = () => {
    history.push('/pages');
  };

  const onCancel = () => {
    if (isFieldsTouched) {
      setPreventNavigation(false);
      openModal(ConfirmationModal, {
        title: formatMessage({
          id: `pages.create_edit_page.${createOrEditMsgId}.cancel_confirmation_modal.title`
        }),
        content: formatMessage({
          id: `pages.create_edit_page.${createOrEditMsgId}.cancel_confirmation_modal.message`
        }),
        confirmButtonLabel: formatMessage({
          id: `pages.create_edit_page.${createOrEditMsgId}.cancel_confirmation_modal.confirm_btn`
        }),
        cancelButtonLabel: formatMessage({
          id: `pages.create_edit_page.${createOrEditMsgId}.cancel_confirmation_modal.cancel_btn`
        }),
        modalAction: goToPagesList
      });
    } else {
      goToPagesList();
    }
  };

  const onFinish = async (values = {}) => {
    setIsLoading(true);

    try {
      const { url } = values;

      const payload = {
        ...values,
        ...contentData
      };

      if (isEdit) {
        const modifiedPayload = getEditChanges({
          beforeChangeObj: fetchedPageDetails,
          afterChangeObj: payload
        });

        if (isEmpty(modifiedPayload)) {
          // Fake payload to call Edit API for empty payload
          modifiedPayload.unset = {
            content: 1
          };
        }
        await editPage(pageId, modifiedPayload);
      } else {
        await createPage(payload);
      }
      setPreventNavigation(false);
      notify({
        msg: formatMessage(
          {
            id: 'pages.create_edit_page.create_page.success_msg'
          },
          {
            pageName: getPageName(values)
          }
        ),
        type: 'success',
        extraAction: {
          action: () =>
            visitPage({
              businessInfo,
              pageURL: url
            }),
          label: formatMessage({
            id: 'pages.actions.visit_page'
          })
        }
      });

      goToPagesList();
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const onConfirm = () => {
    formRef.current.validateFields().then(onFinish);
  };

  const renderHeader = () => (
    <>
      <div className="br-create-edit-page__header__title">
        <Icon
          component={CloseIcon}
          onClick={onCancel}
          className="br-header__go-back-btn ant-icon-md "
        />
        <span className="display-sm">{pageTitle}</span>
      </div>
      <div className="br-create-edit-page__header__actions">
        <BRButton
          label={formatMessage({ id: 'common.cancel' })}
          onClick={onCancel}
        />
        <BRButton
          type="primary"
          label={formatMessage({
            id: `pages.create_edit_page.${
              isEdit ? 'edit_page' : 'create_page'
            }.confirm_btn`
          })}
          disabled={isLoading || isPrimaryBtnDisabled || isContextLoading}
          onClick={onConfirm}
        />
      </div>
    </>
  );

  useEffect(() => {
    if (isEdit) {
      getPageDetails();
    }
  }, []);

  useEffect(() => {
    if (selectedTemplateKey) {
      setContentData(
        getPageTemplate({
          templateKey: selectedTemplateKey,
          businessInfo
        })
      );
      setIsPrimaryBtnDisabled(false);
    }
  }, [selectedTemplateKey]);

  useEffect(() => {
    if (businessInfo?.storeInfo) {
      setIsContextLoading(false);
    }
  }, [businessInfo?.storeInfo]);

  useEffect(() => {
    if (isFieldsTouched) {
      setPreventNavigation(true);
    }
  }, [isFieldsTouched]);

  return (
    <Container
      containerClassName="br-create-edit-page"
      header={<Header headerContent={renderHeader} />}
      content={
        <LoadingWrapper loading={isLoading || isContextLoading}>
          <Form
            scrollToFirstError
            ref={formRef}
            name="create-edit-page-form"
            onValuesChange={onValuesChange}
            className="br-create-edit-page__form"
          >
            <PageDetailsSection
              formRef={formRef}
              urlName={urlName}
              fetchPageDetails={fetchedPageDetails}
            />
            <PageContentSection
              contentData={contentData}
              setContentData={setContentData}
              setSelectedTemplateKey={setSelectedTemplateKey}
              defaultLang={businessInfo?.storeInfo?.defaultLang}
              isEdit={isEdit}
              setIsFieldsTouched={setIsFieldsTouched}
              setIsPrimaryBtnDisabled={setIsPrimaryBtnDisabled}
            />
          </Form>
        </LoadingWrapper>
      }
    />
  );
};

export default CreateEditPage;
